/**
 * SignalR Client Service for Push Notifications
 * 
 * This service manages the SignalR connection to the backend notification hub.
 * It handles connection, disconnection, reconnection, heartbeat, and real-time events.
 */

import * as signalR from '@microsoft/signalr';
import { HubConnectionState } from '@microsoft/signalr';
import { Notification, SignalRConnectionState, SignalRNotificationReadEvent, SignalRNotificationDismissedEvent, SignalRAllNotificationsReadEvent, SignalRUnreadCountUpdateEvent } from '../types/notification.types';

/**
 * SignalR event handlers interface
 */
export interface SignalREventHandlers {
  onReceiveNotification?: (notification: Notification) => void;
  onNotificationRead?: (event: SignalRNotificationReadEvent) => void;
  onNotificationDismissed?: (event: SignalRNotificationDismissedEvent) => void;
  onAllNotificationsRead?: (event: SignalRAllNotificationsReadEvent) => void;
  onUnreadCountUpdate?: (event: SignalRUnreadCountUpdateEvent) => void;
  onConnectionStateChanged?: (state: SignalRConnectionState) => void;
  onError?: (error: Error) => void;
}

/**
 * SignalR Service Configuration
 */
interface SignalRServiceConfig {
  hubUrl: string;
  getAccessToken: () => string | null;
  heartbeatIntervalMs?: number;
  reconnectDelayMs?: number;
  maxReconnectAttempts?: number;
}

/**
 * SignalR Service Class
 */
class SignalRService {
  private connection: signalR.HubConnection | null = null;
  private config: SignalRServiceConfig | null = null;
  private eventHandlers: SignalREventHandlers = {};
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private connectionState: SignalRConnectionState = 'disconnected';
  private isInitialized = false;
  
  // Service-level deduplication to prevent duplicate toast notifications
  private displayedNotifications: Set<number> = new Set();
  private readonly MAX_TRACKED_NOTIFICATIONS = 100;

  /**
   * Check if notification should be processed (deduplication)
   * Returns true if this is a NEW notification (should be processed)
   * Returns false if this is a DUPLICATE (should be ignored)
   */
  private shouldProcessNotification(notificationId: number): boolean {
    // Check if already displayed
    if (this.displayedNotifications.has(notificationId)) {
      console.log('[SignalR] Duplicate notification blocked:', notificationId);
      return false; // Duplicate - don't process
    }
    
    // Mark as displayed
    this.displayedNotifications.add(notificationId);
    
    // Clean up old entries (keep only last 100)
    if (this.displayedNotifications.size > this.MAX_TRACKED_NOTIFICATIONS) {
      const entries = Array.from(this.displayedNotifications);
      const toKeep = entries.slice(-this.MAX_TRACKED_NOTIFICATIONS);
      this.displayedNotifications = new Set(toKeep);
    }
    
    return true; // New notification - process it
  }

  /**
   * Initialize the SignalR service with configuration
   */
  public initialize(config: SignalRServiceConfig, handlers: SignalREventHandlers): void {
    // If already initialized, do nothing (handlers are already registered)
    if (this.isInitialized) {
      console.log('[SignalR] Already initialized, skipping re-initialization');
      return;
    }

    this.config = config;
    this.eventHandlers = handlers;

    // Create SignalR connection
    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(config.hubUrl, {
        accessTokenFactory: () => {
          const token = config.getAccessToken();
          return token || '';
        },
        transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.ServerSentEvents,
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          // Exponential backoff: 0s, 2s, 10s, 30s, then 60s
          if (retryContext.previousRetryCount === 0) return 0;
          if (retryContext.previousRetryCount === 1) return 2000;
          if (retryContext.previousRetryCount === 2) return 10000;
          if (retryContext.previousRetryCount === 3) return 30000;
          return 60000;
        },
      })
      .configureLogging(signalR.LogLevel.Information)
      .build();

    // Register event handlers
    this.registerEventHandlers();

    // Register connection lifecycle handlers
    this.registerConnectionHandlers();

    this.isInitialized = true;
    console.log('[SignalR] Service initialized');
  }

  /**
   * Register SignalR event handlers
   */
  private registerEventHandlers(): void {
    if (!this.connection) return;

    // Receive new notification with service-level deduplication
    this.connection.on('ReceiveNotification', (notification: Notification) => {
      console.log('[SignalR] Received notification:', notification);
      
      // Deduplication check at service level
      if (!this.shouldProcessNotification(notification.id)) {
        return; // Duplicate - ignore
      }
      
      // Call the handler
      this.eventHandlers.onReceiveNotification?.(notification);
    });

    // Notification marked as read
    this.connection.on('NotificationRead', (event: SignalRNotificationReadEvent) => {
      console.log('[SignalR] Notification read:', event);
      this.eventHandlers.onNotificationRead?.(event);
    });

    // Notification dismissed
    this.connection.on('NotificationDismissed', (event: SignalRNotificationDismissedEvent) => {
      console.log('[SignalR] Notification dismissed:', event);
      this.eventHandlers.onNotificationDismissed?.(event);
    });

    // All notifications marked as read
    this.connection.on('AllNotificationsRead', (event: SignalRAllNotificationsReadEvent) => {
      console.log('[SignalR] All notifications read:', event);
      this.eventHandlers.onAllNotificationsRead?.(event);
    });

    // Unread count update
    this.connection.on('UnreadCountUpdate', (event: SignalRUnreadCountUpdateEvent) => {
      console.log('[SignalR] Unread count update:', event);
      this.eventHandlers.onUnreadCountUpdate?.(event);
    });
  }

  /**
   * Register connection lifecycle handlers
   */
  private registerConnectionHandlers(): void {
    if (!this.connection) return;

    this.connection.onclose((error) => {
      console.log('[SignalR] Connection closed', error);
      this.updateConnectionState('disconnected');
      this.stopHeartbeat();
      
      if (error) {
        this.eventHandlers.onError?.(error);
      }
    });

    this.connection.onreconnecting((error) => {
      console.log('[SignalR] Reconnecting...', error);
      this.updateConnectionState('reconnecting');
      this.stopHeartbeat();
      
      if (error) {
        this.eventHandlers.onError?.(error);
      }
    });

    this.connection.onreconnected((connectionId) => {
      console.log('[SignalR] Reconnected with connection ID:', connectionId);
      this.updateConnectionState('connected');
      this.startHeartbeat();
    });
  }

  /**
   * Connect to SignalR hub
   */
  public async connect(): Promise<void> {
    if (!this.connection || !this.config) {
      throw new Error('SignalR service not initialized. Call initialize() first.');
    }

    // Check actual connection state from SignalR
    const actualState = this.connection.state;
    if (actualState !== HubConnectionState.Disconnected) {
      console.log('[SignalR] Connection already in progress or connected. State:', actualState);
      return;
    }

    if (this.connectionState === 'connected' || this.connectionState === 'connecting') {
      console.log('[SignalR] Already connected or connecting');
      return;
    }

    try {
      this.updateConnectionState('connecting');
      await this.connection.start();
      this.updateConnectionState('connected');
      this.startHeartbeat();
      console.log('[SignalR] Connected successfully');
    } catch (error) {
      console.error('[SignalR] Connection failed:', error);
      this.updateConnectionState('disconnected');
      this.eventHandlers.onError?.(error as Error);
      throw error;
    }
  }

  /**
   * Disconnect from SignalR hub
   */
  public async disconnect(): Promise<void> {
    if (!this.connection) return;

    // Check actual connection state
    const actualState = this.connection.state;
    if (actualState === HubConnectionState.Disconnected) {
      console.log('[SignalR] Already disconnected');
      this.updateConnectionState('disconnected');
      return;
    }

    if (this.connectionState === 'disconnecting') {
      console.log('[SignalR] Already disconnecting, waiting...');
      return;
    }

    try {
      this.updateConnectionState('disconnecting');
      
      // Stop heartbeat first to prevent new invocations
      this.stopHeartbeat();
      
      // Give a small delay to allow any pending invocations to complete
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Now stop the connection
      await this.connection.stop();
      this.updateConnectionState('disconnected');
      console.log('[SignalR] Disconnected successfully');
    } catch (error) {
      // Check if it's the "connection closed" error which is expected during disconnect
      const errorMessage = (error as Error).message || '';
      const isExpectedError = 
        errorMessage.includes('Invocation canceled') || 
        errorMessage.includes('connection being closed') ||
        errorMessage.includes('connection is closed') ||
        errorMessage.includes('Cannot send data if the connection is not in the');
      
      if (isExpectedError) {
        // This is expected when disconnecting - connection may already be closed
        console.log('[SignalR] Disconnected (connection already closed - expected during account switch)');
        this.updateConnectionState('disconnected');
        // Don't call onError for expected disconnection scenarios
      } else {
        console.error('[SignalR] Disconnection failed with unexpected error:', error);
        this.updateConnectionState('disconnected'); // Still mark as disconnected
        this.eventHandlers.onError?.(error as Error);
      }
    }
  }

  /**
   * Start heartbeat mechanism
   */
  private startHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
    }

    const intervalMs = this.config?.heartbeatIntervalMs || 30000; // Default 30 seconds

    this.heartbeatInterval = setInterval(async () => {
      // Check both our state and the actual connection state
      if (this.connectionState === 'connected' && 
          this.connection && 
          this.connection.state === 'Connected') {
        try {
          await this.connection.invoke('Heartbeat');
          console.log('[SignalR] Heartbeat sent');
        } catch (error) {
          console.error('[SignalR] Heartbeat failed:', error);
          // Don't call onError for heartbeat failures to avoid noise
          // The connection will be automatically reconnected if needed
        }
      } else {
        console.log('[SignalR] Skipping heartbeat - connection not ready', {
          ourState: this.connectionState,
          actualState: this.connection?.state
        });
      }
    }, intervalMs);

    console.log(`[SignalR] Heartbeat started (interval: ${intervalMs}ms)`);
  }

  /**
   * Stop heartbeat mechanism
   */
  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
      console.log('[SignalR] Heartbeat stopped');
    }
  }

  /**
   * Mark notification as read via SignalR hub
   */
  public async markAsRead(notificationId: number): Promise<void> {
    if (!this.connection || this.connectionState !== 'connected') {
      throw new Error('SignalR not connected');
    }

    try {
      await this.connection.invoke('MarkAsRead', notificationId);
      console.log('[SignalR] Mark as read invoked:', notificationId);
    } catch (error) {
      console.error('[SignalR] Mark as read failed:', error);
      this.eventHandlers.onError?.(error as Error);
      throw error;
    }
  }

  /**
   * Dismiss notification via SignalR hub
   */
  public async dismissNotification(notificationId: number): Promise<void> {
    if (!this.connection || this.connectionState !== 'connected') {
      throw new Error('SignalR not connected');
    }

    try {
      await this.connection.invoke('DismissNotification', notificationId);
      console.log('[SignalR] Dismiss notification invoked:', notificationId);
    } catch (error) {
      console.error('[SignalR] Dismiss notification failed:', error);
      this.eventHandlers.onError?.(error as Error);
      throw error;
    }
  }

  /**
   * Update connection state and notify handlers
   */
  private updateConnectionState(state: SignalRConnectionState): void {
    this.connectionState = state;
    this.eventHandlers.onConnectionStateChanged?.(state);
  }

  /**
   * Get current connection state
   */
  public getConnectionState(): SignalRConnectionState {
    return this.connectionState;
  }

  /**
   * Check if connected
   */
  public isConnected(): boolean {
    return this.connection?.state === HubConnectionState.Connected;
  }

  /**
   * Cleanup resources
   */
  public async cleanup(): Promise<void> {
    await this.disconnect();
    this.stopHeartbeat();
    this.connection = null;
    this.config = null;
    this.eventHandlers = {};
    this.isInitialized = false;
    console.log('[SignalR] Service cleaned up');
  }
}

// Export singleton instance
export const signalRService = new SignalRService();
